import { ReceiptLong, ShoppingCart, TrendingUp } from '@mui/icons-material';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { getBusinessInvoicesSummary } from 'lib/api/business';
import {
  BusinessInvoicesSummary,
  IntervalSummary,
} from 'lib/api/business/types';
import { MONTH_NAMES } from 'lib/consts';
import TableCard from 'lib/designSystem/Card/TableCard';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { chartCalculator } from './Utils/chartCalculator';
import { totalCalculator } from './Utils/totalCalculator';

const BASE_DATA = { dataPayers: { total: [] } };

const HEADERS = [
  {
    key: 'name',
    label: 'Razon Social',
    description: 'Razón social de la empresa',
  },
  { key: 'id', label: 'Rut', description: 'Rut de la empresa' },
  {
    key: 'buyAmount',
    label: 'Compras',
    type: 'money',
    description: 'Monto de las compras',
  },
  {
    key: 'salesAmount',
    label: 'Ventas',
    type: 'money',
    description: 'Monto de las ventas',
  },
  {
    key: 'salesDocuments',
    label: 'N° Ventas',
    description: 'Número de facturas emitidas',
  },
  {
    key: 'salesDocumentsConfirmedPercentage',
    label: 'Acuse',
    type: 'percentage',
    description: 'Porcentaje de facturas con acuse de recibo',
  },
  {
    key: 'salesCancelDocuments',
    label: 'N° Canc.',
    description: 'Número de facturas canceladas',
  },
  {
    key: 'salesCancelDocumentsPercentage',
    label: 'N° Canc. (%)',
    type: 'percentage',
    description: 'Porcentaje de N° de facturas canceladas',
  },
  {
    key: 'salesCancelAmountPercentage',
    label: 'M. Canc. (%)',
    type: 'percentage',
    description: 'Porcentaje del monto de las facturas canceladas',
  },
  {
    key: 'salesCreditNoteDocuments',
    label: 'N° NC ',
    description: 'Número de facturas con NC',
  },
  {
    key: 'salesCreditNoteDocumentsPercentage',
    label: 'N° NC (%)',
    type: 'percentage',
    description: 'Porcentaje de N° de facturas con NC',
  },
  {
    key: 'creditNotePercentage',
    label: 'M. NC (%)',
    type: 'percentage',
    description: 'Porcentaje del monto de las facturas con NC',
  },
];

const DashboardSummary: React.FC = () => {
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [invoicesData, setInvoicesData] =
    useState<BusinessInvoicesSummary>(BASE_DATA);
  const [selectedBusinessFilter, setSelectedBusinessFilter] =
    useState<any>(null);
  const [filteredBusinessTable, setFilteredBusinessTable] = useState<any>([]);
  const [invoiceDataPayersForTable, setInvoiceDataPayersForTable] =
    useState<any>([]);
  const [selectedMonthsInvoices, setSelectedMonthsInvoices] = useState<any>([]);
  const [selectedMonthsCreditNotes, setSelectedMonthsCreditNotes] =
    useState<any>([]);
  const [buyInvoiceAVG, setBuyInvoiceAVG] = useState<any>(0);
  const [buyInvoiceAmount, setBuyInvoiceAmount] = useState<any>(0);
  const [salesInvoiceAVG, setSalesInvoiceAVG] = useState<any>(0);
  const [salesInvoiceAmount, setSalesInvoiceAmount] = useState<any>(0);
  const [salesInvoiceCreditNoteAmount, setSalesInvoiceCreditNoteAmount] =
    useState<any>(0);
  const [salesInvoiceCreditNoteAVG, setSalesInvoiceCReditNoteAVG] =
    useState<any>(0);
  const [selected, setSelected] = useState<any>([]);
  const [chartData, setChartData] = useState<any>([]);
  const [totals, setTotals] = useState<any>({});

  const getSalesWithNCAverage = () => {
    const avg = formatMoney(
      selected.length > 0 ? totals.averageNC : salesInvoiceCreditNoteAVG,
    );
    const percent =
      selected.length > 0
        ? ((totals.salesCreditNoteAmount / totals.salesAmount) * 100).toFixed(2)
        : ((salesInvoiceCreditNoteAmount / salesInvoiceAmount) * 100).toFixed(
            2,
          );
    return { avg, percent };
  };

  const DetailsFilter = (props: any) => {
    const { type } = props;
    const dateSetter =
      type === 'invoice'
        ? setSelectedMonthsInvoices
        : setSelectedMonthsCreditNotes;
    const selectedMonths =
      type === 'invoice' ? selectedMonthsInvoices : selectedMonthsCreditNotes;

    const handleSelectChange = (event: any) => {
      const newSelectedMonths = event.target.value;
      if (newSelectedMonths.length === 0) return dateSetter(['Últimos 12M']);

      if (newSelectedMonths.includes('Últimos 24M')) {
        if (!selectedMonths.includes('Últimos 24M')) {
          return dateSetter(['Últimos 24M']);
        }
      }

      if (newSelectedMonths.includes('Últimos 12M')) {
        if (!selectedMonths.includes('Últimos 12M')) {
          return dateSetter(['Últimos 12M']);
        }
      }

      if (newSelectedMonths.includes('Últimos 6M')) {
        if (!selectedMonths.includes('Últimos 6M')) {
          return dateSetter(['Últimos 6M']);
        }
      }

      if (newSelectedMonths.includes('Últimos 3M')) {
        if (!selectedMonths.includes('Últimos 3M')) {
          return dateSetter(['Últimos 3M']);
        }
      }
      const allSelectedAlready =
        selectedMonths.includes('Últimos 24M') ||
        selectedMonths.includes('Últimos 12M') ||
        selectedMonths.includes('Últimos 6M') ||
        selectedMonths.includes('Últimos 3M');
      if (allSelectedAlready && newSelectedMonths.length > 1) {
        const newSelectedMonthsFiltered = newSelectedMonths.filter(
          (month: string) =>
            month !== 'Últimos 24M' &&
            month !== 'Últimos 12M' &&
            month !== 'Últimos 6M' &&
            month !== 'Últimos 3M',
        );
        return dateSetter(newSelectedMonthsFiltered);
      }
      dateSetter(newSelectedMonths);
    };

    const sortedKeysByDate = Object.keys(invoicesData.dataPayers).sort(
      (a, b) => {
        if (a === 'Últimos 24M') return -1;
        if (b === 'Últimos 24M') return 1;
        const [monthA, yearA] = a.split('-');
        const [monthB, yearB] = b.split('-');
        if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA);
        return MONTH_NAMES.indexOf(monthB) - MONTH_NAMES.indexOf(monthA);
      },
    );

    return (
      <div style={{ width: '100%' }}>
        <FormControl fullWidth>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            multiple
            value={
              type === 'invoice'
                ? selectedMonthsInvoices
                : selectedMonthsCreditNotes
            }
            onChange={handleSelectChange}
            renderValue={(selected) => selected.join(', ')}
          >
            {sortedKeysByDate.map((data: any) => (
              <MenuItem key={data} value={data}>
                <Checkbox
                  checked={
                    (type === 'invoice'
                      ? selectedMonthsInvoices
                      : selectedMonthsCreditNotes
                    ).indexOf(data) > -1
                  }
                />
                <ListItemText primary={data} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };

  const dataPayersToShow = () => {
    const dataHash = invoicesData;
    const monthsToSum = selectedMonthsInvoices;
    const allMonthPayers = monthsToSum.reduce((acc: any, month: any) => {
      return acc.concat(dataHash.dataPayers[month]);
    }, []);
    const mergedMonthsSumByIdentifier = allMonthPayers.reduce(
      (acc: any, payerObject: any) => {
        if (!acc[payerObject.identifier]) {
          acc[payerObject.identifier] = {
            id: payerObject.identifier,
            name: payerObject.name,
            salesAmount: 0,
            salesDocuments: 0,
            salesDocumentsConfirmed: 0,
            salesCancelDocuments: 0,
            buyAmount: 0,
            salesCancelAmount: 0,
            salesCreditNoteAmount: 0,
            salesCreditNoteDocuments: 0,
          };
        }
        acc[payerObject.identifier].salesAmount += payerObject.salesAmount;
        acc[payerObject.identifier].salesDocuments +=
          payerObject.salesDocuments;
        acc[payerObject.identifier].salesDocumentsConfirmed +=
          payerObject.salesDocumentsConfirmed;
        acc[payerObject.identifier].buyAmount += payerObject.buyAmount;
        acc[payerObject.identifier].salesCancelAmount +=
          payerObject.salesCancelAmount;
        acc[payerObject.identifier].salesCancelDocuments +=
          payerObject.salesCancelDocuments;
        acc[payerObject.identifier].salesCreditNoteAmount +=
          payerObject.salesCreditNoteAmount;
        acc[payerObject.identifier].salesCreditNoteDocuments +=
          payerObject.salesCreditNoteDocuments;
        return acc;
      },
      {},
    );
    let newSalesInvoiceCreditNoteAmount = 0;
    Object.keys(mergedMonthsSumByIdentifier).forEach((key) => {
      const payer = mergedMonthsSumByIdentifier[key];
      payer.cancelPercentage = !payer.salesAmount
        ? 0
        : payer.salesCancelAmount / payer.salesAmount;
      payer.salesCancelDocumentsPercentage = !payer.salesCancelDocuments
        ? 0
        : payer.salesCancelDocuments / payer.salesDocuments;
      payer.salesCancelAmountPercentage = !payer.salesAmount
        ? 0
        : payer.salesCancelAmount / payer.salesAmount;
      payer.salesCreditNoteDocumentsPercentage = !payer.salesDocuments
        ? 0
        : payer.salesCreditNoteDocuments / payer.salesDocuments;
      payer.creditNotePercentage = !payer.salesAmount
        ? 0
        : payer.salesCreditNoteAmount / payer.salesAmount;
      payer.salesDocumentsConfirmedPercentage = !payer.salesDocuments
        ? 0
        : payer.salesDocumentsConfirmed / payer.salesDocuments;
      newSalesInvoiceCreditNoteAmount += payer.salesCreditNoteAmount;
    });
    const mergedMonthsSum: IntervalSummary[] = Object.values(
      mergedMonthsSumByIdentifier,
    );
    const buySummary = mergedMonthsSum.reduce(
      (acc: number, payer: IntervalSummary) => acc + payer.buyAmount,
      0,
    );
    const salesSummary = mergedMonthsSum.reduce(
      (acc: number, payer: IntervalSummary) => acc + payer.salesAmount,
      0,
    );
    let monthsToDivide = monthsToSum.length;
    if (monthsToSum[0] === 'Últimos 24M') monthsToDivide = 24;
    else if (monthsToSum[0] === 'Últimos 12M') monthsToDivide = 12;
    else if (monthsToSum[0] === 'Últimos 6M') monthsToDivide = 6;
    else if (monthsToSum[0] === 'Últimos 3M') monthsToDivide = 3;

    setBuyInvoiceAVG(buySummary / monthsToDivide);
    setBuyInvoiceAmount(buySummary);
    setSalesInvoiceAVG(salesSummary / monthsToDivide);
    setSalesInvoiceAmount(salesSummary);
    const table = mergedMonthsSum.map((payer: IntervalSummary) => ({
      ...payer,
    }));
    setInvoiceDataPayersForTable(table);
    setFilteredBusinessTable(table);
    setSelectedBusinessFilter(null);
    setSalesInvoiceCreditNoteAmount(newSalesInvoiceCreditNoteAmount);
    setSalesInvoiceCReditNoteAVG(
      newSalesInvoiceCreditNoteAmount / monthsToDivide,
    );
  };

  useEffect(() => {
    const fetchRelations = async () => {
      if (!id) return;
      const invoices = await getBusinessInvoicesSummary(
        dispatch,
        id,
        'invoices',
      );
      setInvoicesData(invoices || BASE_DATA);
      setSelectedMonthsInvoices(['Últimos 12M']);
      setLoading(false);
    };
    void fetchRelations();
  }, []);

  useEffect(() => {
    dataPayersToShow();
  }, [selectedMonthsInvoices]);

  useEffect(() => {
    setChartData(
      chartCalculator(invoicesData, selected, selected.length === 0),
    );
    setTotals(totalCalculator(invoicesData, selected, selectedMonthsInvoices));
  }, [selected, selectedMonthsInvoices]);

  if (loading) return <CenteredCircularProgress />;

  const handleChangeBusiness = (event: any, newValue: any) => {
    if (!newValue) {
      setSelectedBusinessFilter(null);
      return setFilteredBusinessTable(invoiceDataPayersForTable);
    }
    const filtered = invoiceDataPayersForTable.filter(
      (x: any) => x.id === newValue.id,
    );
    setFilteredBusinessTable(filtered);
    setSelectedBusinessFilter(newValue);
    return;
  };

  const filterBusinesses = () => {
    return (
      <Autocomplete
        style={{ width: '300px' }}
        options={invoiceDataPayersForTable}
        value={selectedBusinessFilter}
        getOptionLabel={(option: { name: string; id: string }) =>
          `${option.name} - (${option.id})`
        }
        renderInput={(params) => (
          <TextField style={{ minWidth: 100 }} {...params} label="Empresa" />
        )}
        onChange={handleChangeBusiness}
        fullWidth
      />
    );
  };

  return (
    <Grid container spacing={2} my={2}>
      <Grid item container spacing={2} xs={12}>
        <Grid item xs={8}>
          <TableCard>
            <SubtitleWithTooltip
              title="Facturas"
              tooltipContent="Información de los últimos 24 meses"
              tooltipPlacement="right"
              variant="compact"
            />
            <LineChart
              data={chartData}
              showArea={false}
              defaultVisibleSeriesKeys={[
                'Venta real (Venta - NC/Reclamo) 12M',
                'Compra últimos 12M',
              ]}
            />
          </TableCard>
        </Grid>
        <Grid item container xs={4}>
          <Grid item xs={12}>
            <DetailsFilter type="invoice" />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Compras total"
              value={formatMoney(
                selected.length > 0 ? totals.buyAmount : buyInvoiceAmount,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.blue}
              icon={<ShoppingCart sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Compras (promedio)"
              value={formatMoney(
                selected.length > 0 ? totals.averageBuy : buyInvoiceAVG,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.teal}
              icon={<ShoppingCart sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Ventas total"
              value={formatMoney(
                selected.length > 0 ? totals.salesAmount : salesInvoiceAmount,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.darkGreen}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Ventas (promedio)"
              value={formatMoney(
                selected.length > 0 ? totals.averageSales : salesInvoiceAVG,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.green}
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Venta con NC"
              value={formatMoney(
                selected.length > 0
                  ? totals.salesCreditNoteAmount
                  : salesInvoiceCreditNoteAmount,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.red}
              icon={<ReceiptLong sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={12}>
            <StatCard
              label="Venta con NC (promedio)"
              value={getSalesWithNCAverage().avg}
              valueLabel={'CLP ' + '(' + getSalesWithNCAverage().percent + '%)'}
              color={BASE_COLORS.orange}
              icon={<ReceiptLong sx={{ fontSize: 24 }} />}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Empresas relacionadas"
            tooltipContent="Puedes filtrar por mes y año con el filtro de arriba. Además puedes seleccionar la empresa que necesites ver."
            tooltipPlacement="right"
            variant="compact"
          >
            {filterBusinesses()}
          </SubtitleWithTooltip>
          <EnhancedTable
            selected={selected}
            setSelected={setSelected}
            finalSumRow
            tableSize="small"
            exportToFile="xlsx"
            dataHeaders={HEADERS}
            data={filteredBusinessTable}
          />
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default DashboardSummary;
